import Phaser from 'phaser';
import { DominoTile } from './DominoTile';
import { getBoardUnitSize } from '../utils/coordinates';
import { COLORS, FONTS, FONT_SIZES, getUIScale } from '../config/theme';
import type { HandTile, ValidPosition } from '../types/game';
import type { Board } from './Board';
import { GAME_DIAGNOSTICS, diag } from '../debug/gameDiagnostics';
import { USE_NATIVE_84X42_HAND_TILES } from '../config/handTileExperiment';
import { getDominoNativeHandTextureKey } from '../config/dominoAssets';
// Cosmetics: durable viewer-skin plumbing (kept when the selection source
// changes). Skin is currently selected by the temporary rule in
// config/viewerCosmetics.ts — see that file for what is and
// isn't temporary about this wiring.
import type { ViewerTileSkin } from '../config/viewerCosmetics';
import { getViewerHandTextureKey } from '../config/viewerTileSkinAssets';
import { t as translate } from '../i18n';

const DRAG_THRESHOLD = 10; // pixels before a press becomes a drag

// ── Drag intent-targeting model ──────────────────────────────────────────────
// While dragging a playable tile, the candidate targets are the placement
// previews plus the tile's original rack slot (cancel/return). Each candidate
// gets a score = pointer distance × zone bias — LOWER wins. The active target
// only changes when a challenger beats it by SWITCH_MARGIN (hysteresis), so
// the selection cannot flicker at zone boundaries. When no placement is in
// range the rack is the only candidate: ambiguity always resolves to return,
// because an accidental placement is worse than an accidental cancellation.
const INTENT = {
  /** Placement lock-in radius, in board-scaled units (mouse). */
  LOCK_RADIUS_UNITS: 2.2,
  /** Touch lock-in radius — finger occlusion means the pointer rarely sits on
   *  the exact ghost. The visible preview stays exact; only this invisible
   *  influence zone is larger. */
  LOCK_RADIUS_UNITS_TOUCH: 3.2,
  /** Beyond this distance (board-scaled units) a placement cannot be active. */
  MAX_PLACEMENT_UNITS: 7,
  /** A challenger must score this factor better than the active target to
   *  take over. */
  SWITCH_MARGIN: 1.3,
  /** Band above the rack top (px) still counted as the hand region. */
  HAND_BAND_PX: 30,
  /** Placements are disfavored while the pointer is in the hand region. */
  HAND_PLACEMENT_MULT: 2.0,
  /** The rack is favored while the pointer is in the hand region… */
  RACK_NEAR_MULT: 0.5,
  /** …and disfavored while the pointer is out in the play area. */
  RACK_FAR_MULT: 1.6,
  /** Inside the lock radius a placement strongly dominates. */
  LOCK_MULT: 0.4,
} as const;

interface HandDragState {
  sprite:         DominoTile;
  tileData:       HandTile;
  originX:        number;
  originY:        number;
  pointerStartX:  number;
  pointerStartY:  number;
  thresholdMet:   boolean;
  ghost:          DominoTile | null;
  dropZones:      Array<{ side: 'left' | 'right'; screenX: number; screenY: number }>;
  rackInsertIdx:  number;
  ghostHandScale: number;
  // Offset from pointer to ghost center, computed once at drag activation.
  // Preserves where the user pressed the tile + adds vertical lift so the
  // ghost is visible above the finger on touch devices.
  grabOffsetX: number;
  grabOffsetY: number;
  // Intent-targeting mode: active when every legal side has server
  // coordinates. Uses the shared Board placement previews instead of the
  // legacy drop-zone circles.
  intentMode:   boolean;
  intentVps:    ValidPosition[];
  intentActive: 'left' | 'right' | 'rack';
}

/**
 * Hand displays the current player's domino tiles at the bottom of the screen.
 * Tiles are rendered VERTICALLY (stacked) like a real domino rack.
 * Tiles are added directly to the scene (not in a container) so click events work reliably.
 */
export class Hand {
  private scene: Phaser.Scene;
  private tiles: HandTile[] = [];
  private tileSprites: DominoTile[] = [];
  private selectedTile: HandTile | null = null;
  private selectedSprite: DominoTile | null = null;
  private interactionEnabled = true;

  // UI elements
  private positionButtons: Phaser.GameObjects.Container | null = null;
  private rackBg: Phaser.GameObjects.Graphics | null = null;

  // Callbacks
  private _onPlayTile: ((tileId: number, position: 'left' | 'right', sourcePos?: { x: number; y: number }, chosenVp?: ValidPosition, inputMethod?: 'click' | 'drag') => void) | null = null;
  private _onPassAvailable: ((available: boolean) => void) | null = null;
  private _onTileSelected: ((tile: HandTile) => void) | null = null;

  // Set to true in destroy() so any callbacks that fire after destroy are no-ops.
  private _destroyed = false;

  /** Whether it's currently the player's turn — drives gold glow on rack. */
  private _isMyTurn = false;
  /** Mobile mode — gates the invalid-tap shake so desktop behavior is unchanged. */
  private _isMobile = false;
  /** Cached rack dimensions for redrawing without full re-render. */
  private _rackDims: { x: number; y: number; w: number; h: number } | null = null;

  /** Y-center of the tile row. Set by GameScene; falls back to bottom-of-screen. */
  private _yAnchor: number | null = null;
  private _slotSpacing: number | null = null;
  private _rackCardVisible = true;
  /** Layout-spec red hand background image (desktop). Hugs the live tiles. */
  private _handBgImg: Phaser.GameObjects.Image | null = null;
  /** Tile scale override. Set by GameScene for portrait phones; falls back to getUIScale(). */
  private _handScale: number | null = null;

  /** Breathing glow overlay drawn behind the rack card during the player's turn. */
  private _turnGlowGfx:   Phaser.GameObjects.Graphics | null = null;
  private _turnGlowTween: Phaser.Tweens.Tween | null = null;

  /** Scene-level tap router — replaces per-sprite pointerdown for reliable mobile touch. */
  private _tapRouter: ((ptr: Phaser.Input.Pointer) => void) | null = null;
  /** World-space bounds for active Left/Right position buttons (set by showPositionSelection). */
  private _posBtnBounds: Array<{ rect: Phaser.Geom.Rectangle; side: 'left' | 'right' }> = [];

  // ─── Drag-to-play system ─────────────────────────────────
  private _boardRef: Board | null = null;
  private _drag: HandDragState | null = null;
  /** Drag ghost kept alive after a successful drop until the board's travel
   *  ghost takes over (destroyed via clearPendingPlayGhost from GameScene).
   *  Bridges the REST/WS round trip so the tile never blinks out at release. */
  private _pendingPlayGhost: DominoTile | null = null;
  private _rackOrder: number[] = [];
  private _tileLayout: { tileW: number; tileH: number; spacing: number; startX: number; y: number } | null = null;
  private _ptrMoveListener: ((ptr: Phaser.Input.Pointer) => void) | null = null;
  private _ptrUpListener:   ((ptr: Phaser.Input.Pointer) => void) | null = null;
  private _escListener:     (() => void) | null = null;

  // Cosmetics: viewer-selected tile skin (durable). Current selection
  // source is temporary — see config/viewerCosmetics.ts.
  private viewerTileSkin: ViewerTileSkin;

  constructor(scene: Phaser.Scene, viewerTileSkin: ViewerTileSkin = 'classic') {
    this.scene = scene;
    this.viewerTileSkin = viewerTileSkin;
    // Desktop: Escape cancels the current click selection (and its board previews).
    this._escListener = () => {
      if (this.selectedTile) this._clearClickSelection();
    };
    this.scene.input.keyboard?.on('keydown-ESC', this._escListener);
  }

  /** Provide a reference to the Board so drop-zone positions can be computed in scene space. */
  setBoard(board: Board): void {
    this._boardRef = board;
  }

  // ─── Layout ──────────────────────────────────────────────

  /**
   * Set where the hand tile row should be centered vertically.
   * Called by GameScene after computing the layout zones.
   */
  /** Layout-spec slot gap in px (config/layoutSpec.ts). null → legacy formula. */
  setSlotSpacing(px: number | null): void {
    this._slotSpacing = px;
  }

  /** Hide the drawn rack card when the scene renders the spec holder image
   *  instead (desktop). The my-turn glow layers are kept either way. */
  setRackCardVisible(visible: boolean): void {
    this._rackCardVisible = visible;
  }

  setYAnchor(y: number): void {
    this._yAnchor = y;
  }

  /**
   * Override the tile scale (e.g. enlarged for portrait phones).
   * When not set, falls back to getUIScale(scene).
   */
  setHandScale(scale: number): void {
    this._handScale = scale;
  }

  /** Enable mobile-specific feedback (invalid-tap shake). Desktop stays silent. */
  setMobile(val: boolean): void {
    this._isMobile = val;
  }

  /**
   * TEMPORARY — native 84×42 hand-tile quality experiment (see
   * src/config/handTileExperiment.ts). Returns the tileScale that, fed
   * through DominoTile's existing `unit = getBoardUnitSize() * tileScale`
   * math, makes the vertical (pre-rotation) display size exactly 84×42 —
   * i.e. zero additional scaling of the native asset. Falls back to the
   * existing responsive scale when the flag is off (unchanged behavior).
   * Centralizes the override so every hand-scale call site (rack render,
   * drag ghost, position-selection buttons) stays in sync.
   */
  private _effectiveHandScale(): number {
    if (USE_NATIVE_84X42_HAND_TILES) {
      const unit = getBoardUnitSize();
      return unit > 0 ? 42 / unit : 1;
    }
    return this._handScale ?? getUIScale(this.scene);
  }

  /** Return the scene-space center of a hand tile sprite, or null if not rendered. */
  getTileScreenPos(tileId: number): { x: number; y: number } | null {
    const sp = this.tileSprites.find(s => s.tileId === tileId);
    return sp ? { x: sp.x, y: sp.y } : null;
  }

  /** The live rack sprite for one tile, or null. Read-only accessor for the
   *  tutorial's action-cue module (scripted mode only); no gameplay effect. */
  getTileSprite(tileId: number): DominoTile | null {
    return this.tileSprites.find(s => s.tileId === tileId) ?? null;
  }

  /** Transform-aware world bounds of one rack tile, or null if not rendered.
   *  Read-only geometry accessor (tutorial spotlight); no gameplay effect. */
  getTileBounds(tileId: number): Phaser.Geom.Rectangle | null {
    const sp = this.tileSprites.find(s => s.tileId === tileId);
    return sp && sp.alpha > 0 ? sp.getBounds() : null;
  }

  /** Union world bounds of the visible rack, or null while empty/hidden.
   *  Read-only geometry accessor (tutorial spotlight); no gameplay effect. */
  getRackBounds(): Phaser.Geom.Rectangle | null {
    let union: Phaser.Geom.Rectangle | null = null;
    for (const sp of this.tileSprites) {
      if (sp.alpha <= 0) continue;
      const b = sp.getBounds();
      union = union ? Phaser.Geom.Rectangle.Union(union, b) : b;
    }
    return union;
  }

  /**
   * Re-show a rack sprite hidden by a play attempt (rollback path — the
   * optimistic ghost has flown back to the rack slot and been destroyed).
   */
  showRackTile(tileId: number): void {
    const sp = this.tileSprites.find(s => s.tileId === tileId);
    sp?.setAlpha(1);
  }

  /**
   * Hide a rack sprite while its optimistic travel ghost is in flight.
   * Idempotent for the drag path (already hidden in _commitDrag); needed for
   * the click path where the sprite is still visible at commit time.
   */
  hideRackTile(tileId: number): void {
    const sp = this.tileSprites.find(s => s.tileId === tileId);
    if (sp) {
      // Freeze in canonical rack state so a rollback reveal is clean even if
      // a select/deselect tween was killed mid-animation.
      this.scene.tweens.killTweensOf(sp);
      sp.setAlpha(0);
      sp.setScale(1);
      if (this._tileLayout) sp.y = this._tileLayout.y;
    }
  }

  /**
   * Destroy the drag ghost held after a successful drop. Called by GameScene
   * once the board's travel ghost exists (same frame — seamless handoff), and
   * on the play-failed path. Safe to call when no ghost is pending.
   */
  clearPendingPlayGhost(): void {
    if (this._pendingPlayGhost) {
      this.scene.tweens.killTweensOf(this._pendingPlayGhost);
      this._pendingPlayGhost.destroy();
      this._pendingPlayGhost = null;
    }
  }

  // ─── Callbacks ───────────────────────────────────────────

  onPlayTile(callback: (tileId: number, position: 'left' | 'right', sourcePos?: { x: number; y: number }, chosenVp?: ValidPosition, inputMethod?: 'click' | 'drag') => void): this {
    this._onPlayTile = callback;
    return this;
  }

  /**
   * Fires whenever the "pass available" state changes:
   *   true  → interaction enabled and player has no legal moves (KnockButton should show)
   *   false → interaction disabled, or player now has moves (KnockButton should hide)
   */
  onPassAvailable(callback: (available: boolean) => void): this {
    this._onPassAvailable = callback;
    return this;
  }

  onTileSelected(callback: (tile: HandTile) => void): this {
    this._onTileSelected = callback;
    return this;
  }

  // ─── Turn State ──────────────────────────────────────────

  /**
   * Tell the rack whether it's the player's turn.
   * Redraws only the rack background — no full re-render.
   */
  setMyTurn(active: boolean): void {
    if (this._isMyTurn === active) return;
    this._isMyTurn = active;
    this.drawRack();
    if (active) {
      this._startTurnGlow();
    } else {
      this._stopTurnGlow();
    }
  }

  /** Draw (or redraw) the rack shelf. Called from renderHand() and setMyTurn(). */
  private drawRack(): void {
    if (!this.rackBg || !this._rackDims) return;
    if (this._destroyed || !this.scene.sys?.displayList) return;

    const { x, y, w, h } = this._rackDims;
    this.rackBg.clear();

    const radius = 12;

    if (this._rackCardVisible) {
      // Translucent dark card — compact, only as wide as the tiles
      this.rackBg.fillStyle(0x0d180d, 0.80);
      this.rackBg.fillRoundedRect(x, y, w, h, radius);

      // Subtle border — no glow by default
      this.rackBg.lineStyle(1, 0x2a4a2a, 0.85);
      this.rackBg.strokeRoundedRect(x, y, w, h, radius);
    }

    // Gold my-turn treatment belongs to the drawn rack card only. In
    // layout-spec mode (desktop, card hidden) the holder artwork + team bg
    // carry the hand zone and the gold wash reads far too bright on top of
    // them (user feedback), so it is skipped entirely there.
    if (this._isMyTurn && this._rackCardVisible) {
      // Soft gold glow above and around the rack card
      [0.028, 0.018, 0.010, 0.005].forEach((alpha, i) => {
        this.rackBg!.fillStyle(COLORS.gold, alpha);
        this.rackBg!.fillRoundedRect(x - i * 2, y - (i + 1) * 4, w + i * 4, h + (i + 1) * 4, radius + i);
      });
      // Gold border on top of glow
      this.rackBg.lineStyle(1.5, COLORS.gold, 0.70);
      this.rackBg.strokeRoundedRect(x, y, w, h, radius);
    }
  }

  /** Start the breathing glow animation around the rack during the player's turn. */
  private _startTurnGlow(): void {
    if (!this._rackDims || this._destroyed || !this.scene.sys?.displayList) return;
    // Layout-spec mode (card hidden): no breathing gold rings either — the
    // rack glow read far too bright over the holder artwork (user feedback).
    if (!this._rackCardVisible) return;
    this._stopTurnGlow();

    const { x, y, w, h } = this._rackDims;
    const radius = 12;
    const gfx = this.scene.add.graphics();
    // Sit just below the rack card (depth 44 = one below rackBg at 45)
    gfx.setDepth(44);

    // Three layered gold rings that grow outward — same layers as drawRack's static glow
    // but at higher alpha so the pulse is visible. The tween drives alpha 0 → 1 → 0.
    [0.55, 0.35, 0.18].forEach((alpha, i) => {
      gfx.fillStyle(COLORS.gold, alpha);
      gfx.fillRoundedRect(x - (i + 1) * 3, y - (i + 1) * 5, w + (i + 1) * 6, h + (i + 1) * 10, radius + i * 2);
    });

    gfx.setAlpha(0);
    this._turnGlowGfx = gfx;

    this._turnGlowTween = this.scene.tweens.add({
      targets:  gfx,
      alpha:    { from: 0, to: 1 },
      duration: 900,
      yoyo:     true,
      repeat:   -1,
      ease:     'Sine.easeInOut',
    });
  }

  /** Stop and destroy the breathing glow. */
  private _stopTurnGlow(): void {
    this._turnGlowTween?.stop();
    this._turnGlowTween = null;
    this._turnGlowGfx?.destroy();
    this._turnGlowGfx = null;
  }

  // ─── Hand Management ─────────────────────────────────────

  setHand(tiles: HandTile[]): void {
    const ids = tiles.map(t => t.id).join(',');
    const currentIds = this.tiles.map(t => t.id).join(',');
    const sameIds = currentIds === ids;

    // Check if valid_positions are also unchanged (same sides, same order)
    const samePositions = sameIds && tiles.every((t, i) => {
      const cur = this.tiles[i];
      if (!cur || t.valid_positions.length !== cur.valid_positions.length) return false;
      return t.valid_positions.every((vp, j) => vp.side === cur.valid_positions[j]?.side);
    });

    if (sameIds && samePositions) {
      // Hand is byte-for-byte identical — skip full re-render, sprites are stable.
      this.tiles = tiles;
      return;
    }

    if (sameIds && !samePositions) {
      // Same tiles but playability changed (board moved) — update highlights in place,
      // no need to destroy/recreate sprites.
      this.tiles = tiles;
      this._syncHighlights();
      return;
    }

    // Tile set actually changed (play was made) — full re-render required.
    this.tiles = tiles;
    this.selectedTile = null;
    this.selectedSprite = null;
    this.clearPositionButtons();
    this.renderHand();
  }

  /** Update the playability highlight on existing sprites without recreating
   *  them. Green cue only while interaction is enabled (the player's turn);
   *  every other tile — and every tile off-turn — is plain. */
  private _syncHighlights(): void {
    for (const sprite of this.tileSprites) {
      const tileData = this.tiles.find(t => t.id === sprite.tileId);
      if (!tileData) continue;
      if (this.interactionEnabled && tileData.valid_positions.length > 0) {
        sprite.highlight();
      } else {
        sprite.unhighlight();
      }
    }
    if (this.interactionEnabled) {
      this._notifyPassAvailable();
    }
  }

  getSelectedTile(): HandTile | null {
    return this.selectedTile;
  }

  enableInteraction(): void {
    this.interactionEnabled = true;
    // Turn started: light up the green cue on every playable tile.
    for (const sprite of this.tileSprites) {
      const tileData = this.tiles.find(t => t.id === sprite.tileId);
      if (tileData && tileData.valid_positions.length > 0) {
        sprite.highlight();
      } else {
        sprite.unhighlight();
      }
    }
    this._notifyPassAvailable();
  }

  disableInteraction(): void {
    this.interactionEnabled = false;
    // Turn change / authoritative update / move submission: cancel any click
    // selection and its board destination previews.
    this._clearClickSelection();
    this._onPassAvailable?.(false);
    if (this._drag) this._cancelDrag();
    // Turn over (or move committed): clear the green cue from every tile so it
    // is only ever shown during the player's own active turn.
    for (const sprite of this.tileSprites) {
      sprite.unhighlight();
    }
  }

  // ─── Rendering ───────────────────────────────────────────

  private renderHand(): void {
    if (this._destroyed) return;
    // scene.add.displayList is null after the Phaser plugin system is torn down —
    // any attempt to create graphics/text would crash. Bail out early.
    if (!this.scene.sys?.displayList) return;

    // Cancel any drag in progress before destroying sprites.
    if (this._drag) {
      this._drag.ghost?.destroy();
      this._drag.sprite.setAlpha(1);
      this._clearDrag();
    }
    // Safety net: a full re-render means the play resolved (or hand changed) —
    // any held drag ghost is stale by now.
    this.clearPendingPlayGhost();
    // Same for a click selection: its sprite is about to be destroyed and its
    // board destination previews were computed for the pre-update board.
    this._clearClickSelection();

    // Tear down the turn glow before destroying rackBg — it will be rebuilt below.
    this._stopTurnGlow();
    // Destroy old sprites
    for (const sprite of this.tileSprites) {
      sprite.destroy();
    }
    this.tileSprites = [];
    this._onPassAvailable?.(false);
    this.clearPositionButtons();
    if (this.rackBg) {
      this.rackBg.destroy();
      this.rackBg = null;
    }
    if (this.tiles.length === 0) {
      this._handBgImg?.destroy();
      this._handBgImg = null;
      return;
    }

    const screenW = this.scene.scale.width;
    const screenH = this.scene.scale.height;

    // ── Sync _rackOrder to the current tile set ──────────────
    // Remove IDs no longer in hand; preserve order; append newcomers at end.
    const currentIds = new Set(this.tiles.map(t => t.id));
    this._rackOrder  = this._rackOrder.filter(id => currentIds.has(id));
    for (const t of this.tiles) {
      if (!this._rackOrder.includes(t.id)) this._rackOrder.push(t.id);
    }
    const orderedTiles = [...this.tiles].sort((a, b) => {
      const ia = this._rackOrder.indexOf(a.id);
      const ib = this._rackOrder.indexOf(b.id);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

    // ── Vertical tile layout — portrait tiles, sized to fill the 78px panel ──
    const s         = getUIScale(this.scene);
    const handScale = this._effectiveHandScale();
    const unit      = getBoardUnitSize() * handScale;
    const tileW     = unit;        // vertical tile: width = 1 unit
    const tileH     = unit * 2;    // vertical tile: height = 2 units (fills panel)

    const spacing     = this._slotSpacing ?? Math.round(4 * handScale);
    const totalWidth  = orderedTiles.length * tileW + (orderedTiles.length - 1) * spacing;
    const startX      = (screenW - totalWidth) / 2 + tileW / 2;
    const bottomMargin = Math.max(8, Math.round(16 * s));     // fallback bottom margin
    // Use the zone anchor set by GameScene; fall back to bottom-of-screen
    const y = this._yAnchor ?? (screenH - tileH / 2 - bottomMargin);

    // Cache tile layout dimensions for rack reorder calculations.
    this._tileLayout = { tileW, tileH, spacing, startX, y };

    // ── Draw rack background — compact card sized for exactly 7 tiles ──
    // Fixed width keeps the rack stable as tiles are played; remaining tiles
    // simply re-center inside the same card.
    const rackPadH    = 8;   // keep in sync with GameScene playAreaBottomY offset
    const rackPadW    = 10;  // horizontal breathing room inside the card
    const MAX_TILES   = 7;
    const rackW       = MAX_TILES * tileW + (MAX_TILES - 1) * spacing + rackPadW * 2;
    const rackX       = (screenW - rackW) / 2;
    const rackY       = y - tileH / 2 - rackPadH;
    const rackH       = tileH + rackPadH * 2;

    this.rackBg = this.scene.add.graphics();
    this.rackBg.setDepth(45);
    this._rackDims = { x: rackX, y: rackY, w: rackW, h: rackH };
    this.drawRack();
    if (this._isMyTurn) this._startTurnGlow();

    // ── Layout-spec red hand background (desktop) — HUGS the live tiles ──
    // Replaces the drawn rack card (hidden via _rackCardVisible) with the
    // team-a-bg image, sized to the CURRENT tile count (not a fixed 7) and
    // centred on the row, so it shrinks with the hand exactly like the
    // opponent cards. Pad ratios come from the design (handBg 676.2x203.4 vs
    // the 7-tile run 639.9 and tile height 163.8 → ~0.22 / ~0.12).
    this._handBgImg?.destroy();
    this._handBgImg = null;
    if (!this._rackCardVisible && this.scene.textures.exists('team-a-bg')) {
      const runW  = orderedTiles.length * tileW + (orderedTiles.length - 1) * spacing;
      // ~8 design units of pad per side (tileW design = 81.9, tileH = 163.8),
      // matching the opponent cards' OPP_CARD_PAD so the hand hugs as tightly.
      const bgW   = runW + tileW * 0.10 * 2;
      const bgH   = tileH + tileH * 0.05 * 2;
      const bg    = this.scene.add.image(screenW / 2, y, 'team-a-bg');
      bg.setDisplaySize(bgW, bgH);
      bg.setDepth(41);   // above the wooden holder (40), below hand tiles (50)
      this._handBgImg = bg;
    }

    for (let i = 0; i < orderedTiles.length; i++) {
      const tileData = orderedTiles[i];
      const x = startX + i * (tileW + spacing);

      const sprite = new DominoTile(
        this.scene,
        x,
        y,
        tileData.id,
        tileData.value_a,
        tileData.value_b,
        'vertical',
        handScale,
        this.viewerTileSkin !== 'classic'
          ? getViewerHandTextureKey(this.viewerTileSkin, tileData.value_a, tileData.value_b)
          : USE_NATIVE_84X42_HAND_TILES
            ? getDominoNativeHandTextureKey(tileData.value_a, tileData.value_b)
            : undefined
      );

      // Set depth so hand tiles are above the board
      sprite.setDepth(50);

      // Tiny horizontal pad only — large pads caused adjacent tile hitboxes to overlap.
      // Vertical pad is slightly more generous since swipe-up misses are common.
      const hitPadX = 3;
      sprite.setInteractive(
        new Phaser.Geom.Rectangle(
          -(tileW / 2 + hitPadX), -(tileH / 2 + 4),
          tileW + hitPadX * 2,    tileH + 8
        ),
        Phaser.Geom.Rectangle.Contains
      );

      // Green playability cue only while it is the player's turn (interaction
      // enabled). Not-my-turn / mid-resolution tiles render plain; the cue is
      // (re)applied by enableInteraction() when the turn actually starts.
      if (this.interactionEnabled && tileData.valid_positions.length > 0) {
        sprite.highlight();
      }

      this.tileSprites.push(sprite);
    }

    // ── Tap router: scene-level listener that manually routes taps to hand tiles ──
    // Per-sprite Container pointerdown is unreliable on mobile touch. This single
    // listener always fires and does its own hit test against the live hitArea.
    //
    // Rack reorder is ALWAYS allowed — even when it is not the player's turn.
    // Play-to-board is gated inside _activateDrag / _commitDrag via interactionEnabled.
    if (this._tapRouter) {
      this.scene.input.off('pointerdown', this._tapRouter);
    }
    this._tapRouter = (ptr: Phaser.Input.Pointer) => {
      const px = ptr.x;
      const py = ptr.y;

      // 1. Position buttons (Left / Right) — only when interaction is enabled
      if (this.interactionEnabled && this._posBtnBounds.length > 0) {
        for (const btn of this._posBtnBounds) {
          if (btn.rect.contains(px, py)) {
            const tileId = this.selectedTile?.id;
            if (tileId == null) return;
            this.confirmPlay(tileId, btn.side, undefined, 'click');
            return;
          }
        }
        // Tapped outside buttons while they are visible — dismiss selection
        this.clearPositionButtons();
        if (this.selectedSprite) { this.selectedSprite.deselect(this._tileLayout?.y); }
        this.selectedTile = null;
        this.selectedSprite = null;
        return;
      }

      // 2. Destination previews — commit the tapped placement. Enlarged touch
      // targets are handled inside hitTestPlacementPreview.
      if (this.interactionEnabled && this.selectedTile && this._boardRef) {
        const vp = this._boardRef.hitTestPlacementPreview(px, py);
        if (vp) {
          this._commitClickPlay(vp);
          return;
        }
      }

      // 3. Hand tiles — always begin drag tracking (for reorder).
      // Whether the tile can be played to the board is determined later.
      for (const sp of this.tileSprites) {
        const ha = sp.input?.hitArea as Phaser.Geom.Rectangle | undefined;
        if (!ha) continue;
        // Transform local hitArea to world space (sprite origin = sp.x, sp.y)
        const haWorld = new Phaser.Geom.Rectangle(sp.x + ha.x, sp.y + ha.y, ha.width, ha.height);
        if (haWorld.contains(px, py)) {
          const liveTile = this.tiles.find(t => t.id === sp.tileId);
          if (!liveTile) continue;
          this._beginDragTracking(liveTile, sp, ptr);
          return;
        }
      }

      // 4. Anywhere else (empty board, placed tiles, panels) while a tile is
      // selected with previews showing — cancel the selection.
      if (this.selectedTile) {
        this._clearClickSelection();
      }
    };
    this.scene.input.on('pointerdown', this._tapRouter);

    // When not my turn: override dim so tiles stay bright regardless of valid_positions
    if (!this.interactionEnabled) {
      for (const sprite of this.tileSprites) {
        sprite.undim();
      }
    }

    if (this.interactionEnabled) {
      this._notifyPassAvailable();
    }
  }

  /** Handle tile click */
  private onTileClicked(tileData: HandTile, sprite: DominoTile): void {
    if (!this.interactionEnabled) {
      // Not our turn — shake the tile on mobile so the tap feels acknowledged
      if (this._isMobile) {
        const ox = sprite.x;
        this.scene.tweens.killTweensOf(sprite);
        this.scene.tweens.add({
          targets: sprite, x: { from: ox - 5, to: ox + 5 },
          duration: 90, yoyo: true, repeat: 0, ease: 'Linear',
          onComplete: () => { sprite.x = ox; },
        });
      }
      return;
    }
    if (tileData.valid_positions.length === 0) return;

    // Deselect previous
    if (this.selectedSprite && this.selectedSprite !== sprite) {
      this.selectedSprite.deselect(this._tileLayout?.y);
      const prevTileData = this.tiles.find(t => t.id === this.selectedSprite?.tileId);
      if (prevTileData && prevTileData.valid_positions.length > 0) {
        this.selectedSprite.highlight();
      }
    }

    // If clicking the same tile, deselect (also clears destination previews)
    if (this.selectedTile?.id === tileData.id) {
      this._clearClickSelection();
      return;
    }

    // Select new tile
    this.selectedTile = tileData;
    this.selectedSprite = sprite;
    sprite.select();

    // Fire tile-selected sound on every tap — covers both the choose-side case
    // (two sides shown) and the auto-play case (one side, plays immediately).
    this._onTileSelected?.(tileData);

    // Show position selection
    this.showPositionSelection(tileData.valid_positions, sprite);
  }

  /**
   * Cancel the current click selection: remove board destination previews,
   * legacy position buttons, and the selected-tile lift. Idempotent.
   */
  private _clearClickSelection(): void {
    this._boardRef?.clearPlacementPreviews();
    this.clearPositionButtons();
    if (this.selectedSprite) {
      this.selectedSprite.deselect(this._tileLayout?.y);
      const td = this.tiles.find(t => t.id === this.selectedSprite!.tileId);
      if (td && td.valid_positions.length > 0 && this.interactionEnabled) {
        this.selectedSprite.highlight();
      }
    }
    this.selectedTile = null;
    this.selectedSprite = null;
  }

  /**
   * Commit a click/tap play for the selected tile with a server-precomputed
   * placement — the same pathway drag uses, so desktop optimistic travel
   * starts synchronously inside confirmPlay → GameScene.handlePlayTile.
   */
  private _commitClickPlay(vp: ValidPosition): void {
    const tile = this.selectedTile;
    if (!tile) return;
    // Launch origin = the sprite's current position (captured before the
    // deselect tween inside _clearClickSelection moves it).
    const sourcePos = this.getTileScreenPos(tile.id) ?? undefined;
    this._clearClickSelection();
    this.confirmPlay(tile.id, vp.side, sourcePos, 'click', vp);
  }

  /** Show exact destination previews, auto-commit a single option, or fall
   *  back to the legacy Left/Right buttons for moves without coordinates. */
  private showPositionSelection(positions: ValidPosition[], sprite: DominoTile): void {
    if (this._destroyed) return;
    this.clearPositionButtons();

    const sides = new Set(positions.map((p) => p.side));

    // One server-precomputed placement per side (engine_failed fallback stubs
    // lack numeric x/y and are excluded — they use the legacy flow below).
    const usableBySide = new Map<'left' | 'right', ValidPosition>();
    for (const p of positions) {
      if (typeof p.x === 'number' && typeof p.y === 'number' && !usableBySide.has(p.side)) {
        usableBySide.set(p.side, p);
      }
    }

    if (usableBySide.size === sides.size && this._boardRef) {
      const usable = [...usableBySide.values()];
      if (usable.length === 1) {
        // ONE legal position: commit immediately — no destination UI, and on
        // desktop the optimistic travel ghost launches in this same frame.
        this._commitClickPlay(usable[0]);
        return;
      }
      // TWO legal positions: exact clickable placements on the board; the tap
      // router resolves clicks via Board.hitTestPlacementPreview.
      this._boardRef.showPlacementPreviews(this.selectedTile!, usable);
      return;
    }

    // ── Legacy server-confirmed flow (no usable coordinates) ─────────────────
    if (sides.size === 1) {
      const side = positions[0].side;
      this.confirmPlay(this.selectedTile!.id, side, undefined, 'click');
      return;
    }

    // Two sides available — show left/right buttons above the tile.
    // Compute tile height the same way renderHand does so buttons always clear the tile.
    const _hs = this._effectiveHandScale();
    const _btnTileH = getBoardUnitSize() * _hs * 2;
    const container = this.scene.add.container(sprite.x, sprite.y - _btnTileH / 2 - 16);
    container.setDepth(200);

    // Left button — display label only; the wire-format 'left'/'right' side
    // values below are protocol identifiers and are never localized.
    const leftBtn = this.createButton(String(translate('gameplay:end.left')), -50, 0, COLORS.teamB);
    container.add(leftBtn);

    // Right button
    const rightBtn = this.createButton(String(translate('gameplay:end.right')), 50, 0, COLORS.teamA);
    container.add(rightBtn);

    // Fade + slide in: start transparent and 8px below final position, animate up
    container.setAlpha(0);
    container.y += 8;
    this.scene.tweens.add({
      targets:  container,
      alpha:    1,
      y:        container.y - 8,
      duration: 120,
      ease:     'Power2',
    });

    this.positionButtons = container;

    // Store world-space bounds for tap router (slightly larger than visual for easy tapping)
    const cx = sprite.x;
    const cy = sprite.y - _btnTileH / 2 - 16;
    const padX = 6;
    const padY = 6;
    this._posBtnBounds = [
      { rect: new Phaser.Geom.Rectangle(cx - 50 - 44 - padX, cy - 22 - padY, 88 + padX * 2, 44 + padY * 2), side: 'left' },
      { rect: new Phaser.Geom.Rectangle(cx + 50 - 44 - padX, cy - 22 - padY, 88 + padX * 2, 44 + padY * 2), side: 'right' },
    ];
  }

  private createButton(
    label: string,
    x: number,
    y: number,
    color: number,
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 0.9);
    bg.fillRoundedRect(-44, -22, 88, 44, 8);
    bg.lineStyle(1, COLORS.gold, 0.5);
    bg.strokeRoundedRect(-44, -22, 88, 44, 8);
    container.add(bg);

    const text = this.scene.add.text(0, 0, label, {
      fontSize: FONT_SIZES.small,
      color: '#ffffff',
      fontStyle: 'bold',
      fontFamily: FONTS.body,
    }).setOrigin(0.5);
    container.add(text);

    return container;
  }

  /** Confirm and emit the play. sourcePos is the scene-space origin for the placement
   *  animation — drag-to-play passes the ghost release point; tap-to-play omits it so
   *  GameScene falls back to the rack tile position. */
  private confirmPlay(tileId: number, position: 'left' | 'right', sourcePos?: { x: number; y: number }, inputMethod?: 'click' | 'drag', chosenVp?: ValidPosition): void {
    if (GAME_DIAGNOSTICS) {
      diag.recordRelease();
      if (inputMethod) diag.recordInputMethod(inputMethod);
    }
    this.clearPositionButtons();
    this.disableInteraction();
    // inputMethod ('click' | 'drag') was already computed for diagnostics —
    // forwarded so consumers can know the gesture (tutorial drag lesson).
    this._onPlayTile?.(tileId, position, sourcePos, chosenVp, inputMethod);
  }

  private clearPositionButtons(): void {
    if (this.positionButtons) {
      this.positionButtons.destroy();
      this.positionButtons = null;
    }
    this._posBtnBounds = [];
  }

  // ─── Pass availability ───────────────────────────────────

  /**
   * Fire _onPassAvailable(true) when interaction is enabled and the player
   * has tiles but zero valid moves — KnockButton should appear.
   * Fire _onPassAvailable(false) otherwise.
   */
  private _notifyPassAvailable(): void {
    if (!this.interactionEnabled) { this._onPassAvailable?.(false); return; }
    const hasAnyValidMove = this.tiles.some((t) => t.valid_positions.length > 0);
    this._onPassAvailable?.(!hasAnyValidMove && this.tiles.length > 0);
  }

  /**
   * Find the tile the player tapped (world-space hit test) and play a brief
   * horizontal shake so they know the tap registered but it isn't their turn.
   * Only called on mobile when interaction is disabled.
   */
  private _shakeTappedTile(px: number, py: number): void {
    for (const sp of this.tileSprites) {
      const ha = sp.input?.hitArea as Phaser.Geom.Rectangle | undefined;
      if (!ha) continue;
      const haWorld = new Phaser.Geom.Rectangle(sp.x + ha.x, sp.y + ha.y, ha.width, ha.height);
      if (!haWorld.contains(px, py)) continue;

      const originX = sp.x;
      this.scene.tweens.killTweensOf(sp);
      // Single left→right→settle shake. Total ~180ms.
      // ±5px amplitude so the motion is clearly visible on a phone.
      this.scene.tweens.add({
        targets:  sp,
        x:        { from: originX - 5, to: originX + 5 },
        duration: 90,
        yoyo:     true,
        repeat:   0,
        ease:     'Linear',
        onComplete: () => { sp.x = originX; },
      });
      break;
    }
  }

  handleResize(): void {
    this.renderHand();
  }

  destroy(): void {
    this._destroyed = true;
    this._rackDims = null;
    this._stopTurnGlow();
    this.clearPendingPlayGhost();
    // Board previews may outlive the Hand during round transitions (the Board
    // is destroyed later, with the scene) — clear them explicitly.
    this._boardRef?.clearPlacementPreviews();
    this.selectedTile = null;
    this.selectedSprite = null;
    if (this._escListener) {
      this.scene.input?.keyboard?.off('keydown-ESC', this._escListener);
      this._escListener = null;
    }
    if (this._drag) {
      this._drag.ghost?.destroy();
      this._clearDrag();
    }
    this._unregisterDragListeners();
    for (const sprite of this.tileSprites) {
      sprite.destroy();
    }
    this.tileSprites = [];
    this._onPassAvailable?.(false);
    this.clearPositionButtons();
    if (this.rackBg) {
      this.rackBg.destroy();
      this.rackBg = null;
    }
    this._handBgImg?.destroy();
    this._handBgImg = null;
    if (this._tapRouter) {
      this.scene.input?.off('pointerdown', this._tapRouter);
      this._tapRouter = null;
    }
    this._boardRef = null;
  }

  // ─── Drag-to-play system ─────────────────────────────────

  private _beginDragTracking(tileData: HandTile, sprite: DominoTile, ptr: Phaser.Input.Pointer): void {
    // Cancel any previous tracking state
    if (this._drag) this._cancelDrag();

    if (GAME_DIAGNOSTICS) diag.recordPointerDown(tileData.id);

    this._drag = {
      sprite,
      tileData,
      originX:        sprite.x,
      originY:        sprite.y,
      pointerStartX:  ptr.x,
      pointerStartY:  ptr.y,
      thresholdMet:   false,
      ghost:          null,
      dropZones:      [],
      rackInsertIdx:  -1,
      ghostHandScale: this._effectiveHandScale(),
      grabOffsetX:    0,
      grabOffsetY:    0,
      intentMode:     false,
      intentVps:      [],
      intentActive:   'rack',
    };

    this._unregisterDragListeners();
    this._ptrMoveListener = (p: Phaser.Input.Pointer) => this._onPointerMove(p);
    this._ptrUpListener   = (p: Phaser.Input.Pointer) => this._onPointerUp(p);
    this.scene.input.on('pointermove', this._ptrMoveListener);
    this.scene.input.on('pointerup',   this._ptrUpListener);
  }

  private _onPointerMove(ptr: Phaser.Input.Pointer): void {
    if (!this._drag) return;
    if (!this._drag.thresholdMet) {
      const dx = ptr.x - this._drag.pointerStartX;
      const dy = ptr.y - this._drag.pointerStartY;
      if (Math.sqrt(dx * dx + dy * dy) >= DRAG_THRESHOLD) {
        this._drag.thresholdMet = true;
        this._activateDrag(ptr);
      }
      return;
    }
    this._updateDrag(ptr);
  }

  private _onPointerUp(ptr: Phaser.Input.Pointer): void {
    if (!this._drag) return;
    if (!this._drag.thresholdMet) {
      // Below drag threshold — treat as a tap
      const { tileData, sprite } = this._drag;
      this._clearDrag();
      this.onTileClicked(tileData, sprite);
    } else {
      this._commitDrag(ptr);
    }
  }

  private _activateDrag(ptr: Phaser.Input.Pointer): void {
    if (!this._drag) return;
    if (GAME_DIAGNOSTICS) diag.recordLiftStart();
    const { sprite, tileData, ghostHandScale } = this._drag;

    // Board drop zones only available when it is our turn and the tile is playable.
    // Dead tiles and off-turn drags are reorder-only — no drop zones, no highlights.
    const canPlay = this.interactionEnabled && tileData.valid_positions.length > 0;
    this._drag.dropZones = canPlay ? this._computeDropZones(tileData) : [];

    // Intent-preview mode: when every legal side has server coordinates, show
    // the shared exact placement previews and drive targeting via intent
    // scoring in _updateDragIntent. Missing coordinates (engine_failed) fall
    // back to the legacy drop-zone circles below.
    if (canPlay && this._boardRef) {
      const usableBySide = new Map<'left' | 'right', ValidPosition>();
      for (const p of tileData.valid_positions) {
        if (typeof p.x === 'number' && typeof p.y === 'number' && !usableBySide.has(p.side)) {
          usableBySide.set(p.side, p);
        }
      }
      const sides = new Set(tileData.valid_positions.map(p => p.side));
      if (usableBySide.size === sides.size) {
        // Any click selection is superseded — its previews are replaced.
        this._clearClickSelection();
        this._drag.intentMode   = true;
        this._drag.intentVps    = [...usableBySide.values()];
        this._drag.intentActive = 'rack';   // the drag starts at the rack
        this._drag.dropZones    = [];       // legacy path disabled
        this._boardRef.showPlacementPreviews(tileData, this._drag.intentVps);
        this._boardRef.setActivePlacementPreview('rack');
      }
    }

    // Grab offset: ghost follows where the user pressed on the tile rather than
    // always centring on the pointer. A vertical lift raises the ghost above the
    // finger on touch devices so it remains visible while dragging.
    const liftPx = this._tileLayout ? this._tileLayout.tileH * 0.5 : 32;
    this._drag.grabOffsetX = sprite.x - ptr.x;
    this._drag.grabOffsetY = sprite.y - ptr.y - liftPx;

    // Leave a faint placeholder in the rack at the tile's original slot
    sprite.setAlpha(0.22);

    // Ghost clone — positioned at the grab-offset location immediately so it
    // never jumps between creation and the first _updateDrag call.
    const ghost = new DominoTile(
      this.scene,
      ptr.x + this._drag.grabOffsetX,
      ptr.y + this._drag.grabOffsetY,
      tileData.id,
      tileData.value_a,
      tileData.value_b,
      'vertical',
      ghostHandScale,
      this.viewerTileSkin !== 'classic'
        ? getViewerHandTextureKey(this.viewerTileSkin, tileData.value_a, tileData.value_b)
        : USE_NATIVE_84X42_HAND_TILES
          ? getDominoNativeHandTextureKey(tileData.value_a, tileData.value_b)
          : undefined
    );
    ghost.setDepth(500);
    ghost.setAlpha(0.92); // slight translucency signals "held, not placed"
    this._drag.ghost = ghost;

    // Subtle lift-off animation
    this.scene.tweens.add({
      targets: ghost, scaleX: 1.10, scaleY: 1.10,
      duration: 120, ease: 'Back.Out',
    });

    // Show board end indicators (only when play is possible)
    if (canPlay && this._boardRef && this._drag.dropZones.length > 0) {
      this._boardRef.showDragHighlights(tileData.valid_positions, null);
    }
  }

  private _updateDrag(ptr: Phaser.Input.Pointer): void {
    if (!this._drag?.ghost) return;
    const { ghost, dropZones, tileData } = this._drag;

    ghost.x = ptr.x + this._drag.grabOffsetX;
    ghost.y = ptr.y + this._drag.grabOffsetY;

    if (this._drag.intentMode) {
      this._updateDragIntent(ptr);
    } else {
      // ── Legacy drop-zone targeting (engine_failed moves only) ──────────────
      const radius = this._dropRadius();
      const rSq    = radius * radius;
      let nearest: typeof dropZones[0] | null = null;
      let minDist  = rSq;
      for (const zone of dropZones) {
        const dx = ptr.x - zone.screenX;
        const dy = ptr.y - zone.screenY;
        const d  = dx * dx + dy * dy;
        if (d < minDist) { minDist = d; nearest = zone; }
      }

      // Update board highlights (no-op when dropZones is empty, i.e. off-turn or dead tile)
      if (this._boardRef && dropZones.length > 0) {
        this._boardRef.showDragHighlights(tileData.valid_positions, nearest?.side ?? null);
      }
    }

    // Rack reorder preview
    if (this._tileLayout && ptr.y >= this._tileLayout.y - this._tileLayout.tileH / 2 - 20) {
      this._updateRackReorder(ptr.x);
    } else {
      this._snapRackToOrder(tileData.id); // restore non-dragged tiles to their canonical positions
    }
  }

  /**
   * Intent scoring for drag targeting (see INTENT constants for the model).
   * Exactly one of {placement A, placement B, rack return} is active at any
   * time; the result drives the preview/rack visuals and the release commit.
   */
  private _updateDragIntent(ptr: Phaser.Input.Pointer): void {
    const drag = this._drag;
    if (!drag || !this._boardRef) return;

    const boardScale = this._boardRef.scaleX || 1;
    const unit  = getBoardUnitSize() * boardScale;
    const lockR = unit * (this._isMobile ? INTENT.LOCK_RADIUS_UNITS_TOUCH : INTENT.LOCK_RADIUS_UNITS);
    const maxR  = unit * INTENT.MAX_PLACEMENT_UNITS;
    const rackTopY = this._tileLayout
      ? this._tileLayout.y - this._tileLayout.tileH / 2 - INTENT.HAND_BAND_PX
      : Number.POSITIVE_INFINITY;
    const inHand = ptr.y >= rackTopY;

    // Candidate scores — lower wins.
    const scores = new Map<'left' | 'right' | 'rack', number>();
    for (const t of this._boardRef.getPlacementPreviewTargets()) {
      const d = Phaser.Math.Distance.Between(ptr.x, ptr.y, t.x, t.y);
      if (d > maxR) continue;                        // out of placement range
      let s = d;
      if (inHand) s *= INTENT.HAND_PLACEMENT_MULT;   // return favored near the rack
      if (d <= lockR) s *= INTENT.LOCK_MULT;         // nearby ghost strongly dominates
      scores.set(t.side, s);
    }
    const dRack = Phaser.Math.Distance.Between(ptr.x, ptr.y, drag.originX, drag.originY);
    scores.set('rack', dRack * (inHand ? INTENT.RACK_NEAR_MULT : INTENT.RACK_FAR_MULT));

    let bestKey: 'left' | 'right' | 'rack' = 'rack';
    let bestScore = Number.POSITIVE_INFINITY;
    for (const [k, s] of scores) {
      if (s < bestScore) { bestScore = s; bestKey = k; }
    }

    // Hysteresis: the active target keeps its status unless the challenger is
    // meaningfully better — or the active target dropped out of candidacy.
    const activeScore = scores.get(drag.intentActive);
    if (activeScore === undefined) {
      drag.intentActive = bestKey;
    } else if (bestKey !== drag.intentActive && bestScore * INTENT.SWITCH_MARGIN < activeScore) {
      drag.intentActive = bestKey;
    }

    // Visual feedback: brighten the active ghost / dim the others, and let the
    // rack placeholder glow slightly while return is the active intent.
    this._boardRef.setActivePlacementPreview(drag.intentActive);
    drag.sprite.setAlpha(drag.intentActive === 'rack' ? 0.5 : 0.22);
  }

  /**
   * Keep the drag ghost alive across the REST/WS round trip as the pending
   * play ghost with the "stamp" settle. When optimistic travel starts, the
   * GameScene destroys it in the same frame via clearPendingPlayGhost().
   */
  private _holdGhostForPlay(ghost: DominoTile): void {
    this._pendingPlayGhost?.destroy();
    this._pendingPlayGhost = ghost;
    // IMPACT (~110ms): compress 1.10 → 0.97, rebound to 1.0, solidify to full
    // opacity — then confident stillness until the travel ghost takes over.
    this.scene.tweens.killTweensOf(ghost);
    this.scene.tweens.add({
      targets:  ghost,
      scaleX:   0.97,
      scaleY:   0.97,
      alpha:    1,
      duration: 60,
      ease:     'Power2.easeIn',
      onComplete: () => {
        if (!ghost.active) return;
        this.scene.tweens.add({
          targets:  ghost,
          scaleX:   1,
          scaleY:   1,
          duration: 50,
          ease:     'Power2.easeOut',
        });
      },
    });
  }

  private _commitDrag(ptr: Phaser.Input.Pointer): void {
    if (!this._drag) return;
    const { ghost, dropZones, tileData, sprite, rackInsertIdx, originX, originY } = this._drag;

    // ── Board play — intent mode (shared placement previews) ─────────────────
    // Release resolves to whatever target is currently active. Rack or
    // ambiguous → fall through to the rack settle below: no POST, no travel.
    if (this._drag.intentMode) {
      const active = this._drag.intentActive;
      this._boardRef?.clearPlacementPreviews();
      if (active !== 'rack' && this.interactionEnabled) {
        const vp = this._drag.intentVps.find(p => p.side === active);
        if (vp) {
          const dragSourcePos = ghost ? { x: ghost.x, y: ghost.y } : { x: ptr.x, y: ptr.y };
          if (ghost) this._holdGhostForPlay(ghost);
          sprite.setAlpha(0);
          this._clearDrag();
          this.confirmPlay(tileData.id, vp.side, dragSourcePos, 'drag', vp);
          return;
        }
      }
    }

    // ── Board play — legacy drop zones (engine_failed moves only) ────────────
    if (this.interactionEnabled && dropZones.length > 0) {
      const rSq = this._dropRadius() ** 2;
      let bestZone: typeof dropZones[0] | null = null;
      let minDist  = rSq;
      for (const zone of dropZones) {
        const dx = ptr.x - zone.screenX;
        const dy = ptr.y - zone.screenY;
        const d  = dx * dx + dy * dy;
        if (d < minDist) { minDist = d; bestZone = zone; }
      }
      if (bestZone) {
        // Travel source = the ghost's visual position at release, so the board
        // placement animation starts exactly where the user let go.
        const dragSourcePos = ghost ? { x: ghost.x, y: ghost.y } : { x: ptr.x, y: ptr.y };
        if (ghost) this._holdGhostForPlay(ghost);
        sprite.setAlpha(0);
        this._clearDrag();
        // Server-precomputed placement for the chosen side — powers desktop
        // optimistic travel. engine_failed fallback stubs lack x/y and are
        // filtered out here, so those moves keep the server-confirmed flow.
        const chosenVp = tileData.valid_positions.find(p =>
          p.side === bestZone!.side &&
          typeof p.x === 'number' && typeof p.y === 'number'
        );
        this.confirmPlay(tileData.id, bestZone.side, dragSourcePos, 'drag', chosenVp);
        return;
      }
    }

    // ── Rack drop: commit reorder if pointer is inside the rack area ─────────
    const layout = this._tileLayout;
    const inRack = layout != null && ptr.y >= layout.y - layout.tileH / 2 - 20;
    if (inRack && rackInsertIdx >= 0) {
      this._finalizeRackReorder(tileData.id, rackInsertIdx);
    }
    // _rackOrder is now authoritative (updated on rack drop, unchanged on cancel).

    // ── Settle: animate ghost to the tile's canonical position ───────────────
    // Compute final slot from _rackOrder (gives originX unchanged on cancel).
    const finalIdx = layout ? this._rackOrder.indexOf(tileData.id) : -1;
    const finalX   = layout && finalIdx >= 0
      ? layout.startX + finalIdx * (layout.tileW + layout.spacing)
      : originX;
    const finalY   = layout ? layout.y : originY;

    // Snap all other sprites to their canonical positions immediately.
    this._snapRackToOrder(tileData.id);

    // Keep sprite hidden until the ghost lands (avoids double-image flash).
    sprite.setAlpha(0);
    sprite.x = finalX;
    sprite.y = finalY;

    if (ghost) {
      this.scene.tweens.add({
        targets: ghost, x: finalX, y: finalY, scaleX: 1, scaleY: 1,
        duration: 200, ease: 'Power2.easeOut',
        onComplete: () => { ghost.destroy(); sprite.setAlpha(1); },
      });
    } else {
      sprite.setAlpha(1);
    }
    this._clearDrag();
  }

  private _cancelDrag(): void {
    if (!this._drag) return;
    const { ghost, sprite, tileData, originX, originY } = this._drag;
    if (this._drag.intentMode) this._boardRef?.clearPlacementPreviews();
    sprite.setAlpha(1);
    // Restore all other sprites to their canonical positions (dragged sprite stays at originX/Y).
    this._snapRackToOrder(tileData.id);
    if (ghost) {
      this.scene.tweens.add({
        targets: ghost, x: originX, y: originY, scaleX: 1, scaleY: 1,
        duration: 250, ease: 'Back.Out',
        onComplete: () => ghost.destroy(),
      });
    }
    this._clearDrag(); // clears highlights via _clearDrag → clearDragHighlights
  }

  private _clearDrag(): void {
    this._unregisterDragListeners();
    this._drag = null;
    this._boardRef?.clearDragHighlights();
  }

  private _unregisterDragListeners(): void {
    if (this._ptrMoveListener) {
      this.scene.input?.off('pointermove', this._ptrMoveListener);
      this._ptrMoveListener = null;
    }
    if (this._ptrUpListener) {
      this.scene.input?.off('pointerup', this._ptrUpListener);
      this._ptrUpListener = null;
    }
  }

  private _computeDropZones(tileData: HandTile): Array<{ side: 'left' | 'right'; screenX: number; screenY: number }> {
    if (!this._boardRef || tileData.valid_positions.length === 0) return [];
    const seen  = new Set<'left' | 'right'>();
    const zones: Array<{ side: 'left' | 'right'; screenX: number; screenY: number }> = [];
    for (const vp of tileData.valid_positions) {
      if (seen.has(vp.side)) continue;
      seen.add(vp.side);
      const sc = this._boardRef.engineToSceneScreen(vp.x, vp.y);
      zones.push({ side: vp.side, screenX: sc.x, screenY: sc.y });
    }
    return zones;
  }

  /** Hit-detection radius in scene-space pixels, accounting for board zoom. */
  private _dropRadius(): number {
    const unit  = getBoardUnitSize();
    const scale = this._boardRef ? this._boardRef.scaleX : 1;
    return unit * scale * 2.2;
  }

  private _updateRackReorder(ptrX: number): void {
    if (!this._drag || !this._tileLayout) return;
    const { tileData }              = this._drag;
    const { tileW, spacing }        = this._tileLayout;
    const nonDragged = this.tileSprites.filter(s => s.tileId !== tileData.id);

    // Insert before the first non-dragged tile whose visual centre is right of ptrX.
    // Comparing against sp.x (current animated position) is more accurate than using
    // original slot indices, which are wrong whenever the dragged tile is not the last tile.
    let insertIdx = nonDragged.length;
    for (let i = 0; i < nonDragged.length; i++) {
      if (ptrX < nonDragged[i].x) { insertIdx = i; break; }
    }
    this._drag.rackInsertIdx = insertIdx;

    // Shift non-dragged tiles to leave a gap at insertIdx
    const totalSlots = nonDragged.length + 1;
    const newStartX  = (this.scene.scale.width - (totalSlots * tileW + (totalSlots - 1) * spacing)) / 2 + tileW / 2;
    for (let i = 0; i < nonDragged.length; i++) {
      const slot    = i >= insertIdx ? i + 1 : i;
      const targetX = newStartX + slot * (tileW + spacing);
      const sp      = nonDragged[i];
      if (Math.abs(sp.x - targetX) > 1) {
        this.scene.tweens.killTweensOf(sp);
        this.scene.tweens.add({ targets: sp, x: targetX, duration: 120, ease: 'Power2' });
      }
    }
  }

  /**
   * Animate all sprites (except the one currently being dragged) to the positions
   * implied by _rackOrder. Safe to call at any time — kills any in-flight tweens
   * and re-targets. Used on drag cancel, drag commit, and when pointer leaves rack.
   */
  private _snapRackToOrder(exceptId?: number): void {
    if (!this._tileLayout) return;
    const { startX, tileW, spacing, y } = this._tileLayout;
    for (let i = 0; i < this._rackOrder.length; i++) {
      const id = this._rackOrder[i];
      if (id === exceptId) continue;
      const sp = this.tileSprites.find(s => s.tileId === id);
      if (!sp) continue;
      const targetX = startX + i * (tileW + spacing);
      sp.setAlpha(1);
      this.scene.tweens.killTweensOf(sp);
      if (Math.abs(sp.x - targetX) > 0.5 || Math.abs(sp.y - y) > 0.5) {
        this.scene.tweens.add({ targets: sp, x: targetX, y, duration: 150, ease: 'Power2.easeOut' });
      }
    }
    if (this._drag) this._drag.rackInsertIdx = -1;
  }

  private _finalizeRackReorder(draggedId: number, insertIdx: number): void {
    const nonDraggedIds = this.tileSprites
      .filter(s => s.tileId !== draggedId)
      .map(s => s.tileId);
    const newOrder = [...nonDraggedIds];
    newOrder.splice(insertIdx, 0, draggedId);
    this._rackOrder = newOrder;
  }
}
